import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ListNumber from '../../../floorCovering/ListNumber'

// SCALE_BY_DENSITY maps list density (max of entry count and total materials) to
// a scale: a 1.0 plateau for normal/heavy pages, 0.9 for very dense pages, and
// the MIN_CELL_SCALE 0.8 floor for extreme pages. Anchored on real brochure data:
// the reported worst page (~23) stays at 1.0; the densest real page (~69) hits the
// 0.8 floor. The DefaultPage overflow guard still backstops anything past 0.8.
const MIN_CELL_SCALE = 0.8
const MAX_CELL_SCALE = 2.0
const SCALE_BY_DENSITY = [
  { max: 2, scale: 2.0 },
  { max: 6, scale: 1.5 },
  { max: 24, scale: 1.0 },
  { max: 46, scale: 0.9 },
]
const VERTICAL_EXPANSION_BY_DENSITY = [
  { max: 2, multiplier: 1.5 },
  { max: 4, multiplier: 1.2 },
  { max: 12, multiplier: 1.0 },
]

const BASE_CELL_PADDING_Y = 4
const BASE_CELL_PADDING_X = 6
const BASE_GROUPED_SWATCH_GAP = 8
const BASE_POINT_BADGE_GAP = 6
const BASE_MATERIAL_FONT_SIZE = 10
const BASE_SWATCH_LABEL_FONT_SIZE = 9
const BASE_CHIP_SIZE = 18
const BASE_ROW_MIN_HEIGHT = 34

const useStyles = makeStyles({
  list: {
    display: 'grid',
    gap: '6px 10px',
    alignItems: 'stretch',
    gridAutoFlow: 'row',
    gridAutoRows: '1fr',
    width: '100%',
  },
  groupedRow: {
    display: 'grid',
    alignItems: 'stretch',
    minWidth: 0,
    height: '100%',
  },
  pointRow: {
    display: 'grid',
    alignItems: 'stretch',
    minWidth: 0,
    height: '100%',
  },
  pointBadgeWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textBlock: {
    minWidth: 0,
  },
  materialNames: {
    color: '#111',
    fontWeight: 600,
    lineHeight: 1.2,
    overflowWrap: 'break-word',
  },
  swatchLabel: {
    color: '#222',
    lineHeight: 1.2,
    overflowWrap: 'break-word',
  },
  scaledContent: {
    display: 'grid',
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    height: '100%',
  },
  chip: {
    border: '1px solid #777',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  pointBadge: {
    margin: 0,
    flexShrink: 0,
  },
})

const valueOrInfinity = (value) =>
  value === null || value === undefined ? Number.POSITIVE_INFINITY : value

const sortByListOrderThenPointNumber = (a, b) => {
  const listOrderDifference =
    valueOrInfinity(a.listOrder) - valueOrInfinity(b.listOrder)

  if (listOrderDifference !== 0) {
    return listOrderDifference
  }

  return valueOrInfinity(a.pointNumber) - valueOrInfinity(b.pointNumber)
}

const getLowestSortValue = (materials) => {
  const listOrders = materials
    .map((material) => material.listOrder)
    .filter((listOrder) => listOrder !== null && listOrder !== undefined)

  if (listOrders.length > 0) {
    return Math.min(...listOrders)
  }

  return Math.min(
    ...materials.map((material) => valueOrInfinity(material.pointNumber))
  )
}

const getColumnTemplate = (entryCount) => {
  if (entryCount < 2) {
    return '1fr'
  }

  if (entryCount < 5) {
    return 'repeat(2, minmax(0, 1fr))'
  }

  return 'repeat(3, minmax(0, 1fr))'
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const getCellScale = (density) => {
  const tier = SCALE_BY_DENSITY.find(({ max }) => density <= max)
  const scale = tier ? tier.scale : MIN_CELL_SCALE

  return clamp(scale, MIN_CELL_SCALE, MAX_CELL_SCALE)
}

const getVerticalExpansionMultiplier = (density) => {
  const tier = VERTICAL_EXPANSION_BY_DENSITY.find(({ max }) => density <= max)

  return tier ? tier.multiplier : 1
}

// entries in point mode are per-material; in grouped mode each entry is a
// color group holding 1+ materials whose names are joined into one (tall) cell.
// Because all cells render at equal height, a single material-dense swatch must
// drive the scale just as strongly as many separate swatches would.
const getListDensity = (entries, hasDescriptionPoints) => {
  if (hasDescriptionPoints) {
    return entries.length
  }

  const totalMaterials = entries.reduce(
    (sum, group) => sum + group.materials.length,
    0
  )

  return Math.max(entries.length, totalMaterials)
}

const getSwatchLabel = (colorObject) => {
  if (!colorObject) {
    return ''
  }

  const prefix =
    colorObject.color_vendor_product_info?.product_identifier_prefix
  const { identifier } = colorObject

  if (prefix && identifier) {
    return `${prefix} ${identifier} - ${colorObject.name}`
  }

  if (identifier) {
    return `${identifier} - ${colorObject.name}`
  }

  return colorObject.name || ''
}

const normalizeGroups = (groupedMaterials) =>
  Object.values(groupedMaterials).map(([colorObject, ...materials]) => ({
    colorObject,
    materials: materials.filter(Boolean),
  }))

export const getBrochureMaterialPointEntries = (groupedMaterials) =>
  normalizeGroups(groupedMaterials)
    .flatMap((group) =>
      group.materials.map((material) => ({
        colorObject: group.colorObject,
        material,
      }))
    )
    .filter((entry) => entry.material.pointPosition !== null)
    .sort((a, b) => sortByListOrderThenPointNumber(a.material, b.material))
    .map((entry, index) => ({
      ...entry,
      displayNumber: index + 1,
    }))

export const getBrochureDescriptionPoints = (groupedMaterials) =>
  getBrochureMaterialPointEntries(groupedMaterials).map(
    ({ material, displayNumber }) => ({
      ...material,
      pointNumber: displayNumber,
    })
  )

export default function BrochureMaterialList({ groupedMaterials }) {
  const classes = useStyles()
  const groups = normalizeGroups(groupedMaterials)
  const hasDescriptionPoints = groups.some((group) =>
    group.materials.some((material) => material.pointPosition !== null)
  )

  const entries = hasDescriptionPoints
    ? getBrochureMaterialPointEntries(groupedMaterials)
    : groups
        .filter((group) => group.materials.length > 0)
        .sort(
          (a, b) =>
            getLowestSortValue(a.materials) - getLowestSortValue(b.materials)
        )

  if (entries.length === 0) {
    return null
  }

  const density = getListDensity(entries, hasDescriptionPoints)
  const scale = getCellScale(density)
  const verticalExpansionMultiplier = getVerticalExpansionMultiplier(density)
  const scaledMaterialFontSize = BASE_MATERIAL_FONT_SIZE * scale
  const scaledSwatchLabelFontSize = BASE_SWATCH_LABEL_FONT_SIZE * scale
  const scaledChipSize = BASE_CHIP_SIZE * scale
  const scaledCellPaddingY =
    BASE_CELL_PADDING_Y * scale * verticalExpansionMultiplier
  const scaledCellPaddingX = BASE_CELL_PADDING_X * scale
  const scaledGroupedSwatchGap = BASE_GROUPED_SWATCH_GAP * scale
  const scaledPointBadgeGap = BASE_POINT_BADGE_GAP * scale
  const scaledRowMinHeight =
    BASE_ROW_MIN_HEIGHT * scale * verticalExpansionMultiplier
  const cellStyle = {
    minHeight: `${scaledRowMinHeight}px`,
    padding: `${scaledCellPaddingY}px ${scaledCellPaddingX}px`,
  }
  const groupedRowStyle = {
    ...cellStyle,
    gridTemplateColumns: 'minmax(0, 1fr)',
  }
  const pointRowStyle = {
    ...cellStyle,
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    columnGap: `${scaledPointBadgeGap}px`,
  }
  const scaledContentStyle = {
    columnGap: `${scaledGroupedSwatchGap}px`,
    gridTemplateColumns: `${scaledChipSize}px minmax(0, 1fr)`,
  }
  const chipStyle = (hex) => ({
    backgroundColor: `#${hex || 'ffffff'}`,
    height: `${scaledChipSize}px`,
    width: `${scaledChipSize}px`,
  })
  const materialNamesStyle = {
    fontSize: `${scaledMaterialFontSize}px`,
  }
  const swatchLabelStyle = {
    fontSize: `${scaledSwatchLabelFontSize}px`,
  }

  return (
    <div
      className={classes.list}
      style={{ gridTemplateColumns: getColumnTemplate(entries.length) }}
    >
      {hasDescriptionPoints
        ? entries.map(({ colorObject, material, displayNumber }) => (
            <div
              className={classes.pointRow}
              key={`${colorObject?.id}-${material.materialId}-${displayNumber}`}
              style={pointRowStyle}
            >
              <div className={classes.pointBadgeWrap}>
                <ListNumber
                  number={displayNumber}
                  customClass={classes.pointBadge}
                />
              </div>
              <div className={classes.scaledContent} style={scaledContentStyle}>
                <span
                  aria-hidden="true"
                  className={classes.chip}
                  style={chipStyle(colorObject?.hex)}
                />
                <div className={classes.textBlock}>
                  <div
                    className={classes.materialNames}
                    style={materialNamesStyle}
                  >
                    {material.name}
                  </div>
                  <div className={classes.swatchLabel} style={swatchLabelStyle}>
                    {getSwatchLabel(colorObject)}
                  </div>
                </div>
              </div>
            </div>
          ))
        : entries.map((group) => {
            const materialNames = [...group.materials]
              .sort(sortByListOrderThenPointNumber)
              .map((material) => material.name)
              .join(', ')

            return (
              <div
                className={classes.groupedRow}
                key={group.colorObject?.id || materialNames}
                style={groupedRowStyle}
              >
                <div
                  className={classes.scaledContent}
                  style={scaledContentStyle}
                >
                  <span
                    aria-hidden="true"
                    className={classes.chip}
                    style={chipStyle(group.colorObject?.hex)}
                  />
                  <div className={classes.textBlock}>
                    <div
                      className={classes.materialNames}
                      style={materialNamesStyle}
                    >
                      {materialNames}
                    </div>
                    <div
                      className={classes.swatchLabel}
                      style={swatchLabelStyle}
                    >
                      {getSwatchLabel(group.colorObject)}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
    </div>
  )
}

BrochureMaterialList.propTypes = {
  groupedMaterials: PropTypes.object.isRequired,
}
